import React, { useEffect, useState, useRef } from "react";
import { doc, getDoc, setDoc, collection, getDocs, query, where, orderBy, limit } from "firebase/firestore";
import { db } from "../firebase";
import { Icons } from '../lib/Icons';
import { getStudentEhcpStoragePath, getStudentEhcpSummaryDocId } from "../lib/ehcpPath";

const EHCPExtractedTargets = ({ student, onProcessingComplete, hideSuccessMessages = false }) => {
  const [targets, setTargets] = useState("");
  const [targetsByArea, setTargetsByArea] = useState({
    communicationAndInteraction: [],
    cognitionAndLearning: [],
    socialEmotionalMentalHealth: [],
    sensoryAndPhysical: [],
  });
  const [loading, setLoading] = useState(true);
  // Manual-only mode UI, but keep states so code compiles
  const [processing, setProcessing] = useState(false);
  const [showTargets, setShowTargets] = useState(true);
  const [polling, setPolling] = useState(false);
  const [pollingCount, setPollingCount] = useState(0);
  const pollingCountRef = useRef(0);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [processingStatus, setProcessingStatus] = useState("");
  const pollingIntervalRef = useRef(null);

  // Manual entry mode state — hidden until staff choose to edit manually
  const [manualMode, setManualMode] = useState(false);
  const [manualCni, setManualCni] = useState("");
  const [manualCal, setManualCal] = useState("");
  const [manualSemh, setManualSemh] = useState("");
  const [manualSp, setManualSp] = useState("");
  const [savingManual, setSavingManual] = useState(false);

  // Normalize different backend shapes into a single UI shape
  // Supports both old format (camelCase) and new format (from our Cloud Functions)
  const normalizeTargetsByArea = (tba) => {
    const src = tba || {};
    
    // Handle "No outcomes provided" string
    const parseOutcomes = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value === 'No outcomes provided' ? [] : [value];
      }
      return Array.isArray(value) ? value : [];
    };
    
    // Try new format first (from Cloud Functions), then fall back to old format
    const cni = parseOutcomes(
      src.CommunicationInteraction || src.communicationAndInteraction
    );
    const cal = parseOutcomes(
      src.CognitionLearning || src.cognitionAndLearning
    );
    const semh = parseOutcomes(
      src.SEMH || src.socialEmotionalMentalHealth || src.semh
    );
    const sp = parseOutcomes(
      src.SensoryPhysical || src.sensoryAndPhysical || src.sensoryAndOrPhysical
    );
    
    return {
      communicationAndInteraction: cni,
      cognitionAndLearning: cal,
      socialEmotionalMentalHealth: semh,
      sensoryAndPhysical: sp,
    };
  };

  const prefillManualFromCurrent = () => {
    setManualCni((targetsByArea.communicationAndInteraction || []).join("\n"));
    setManualCal((targetsByArea.cognitionAndLearning || []).join("\n"));
    setManualSemh((targetsByArea.socialEmotionalMentalHealth || []).join("\n"));
    setManualSp((targetsByArea.sensoryAndPhysical || []).join("\n"));
  };

  const pupilDisplayName = [student?.firstName, student?.lastName].filter(Boolean).join(" ").trim()
    || student?.displayName
    || student?.name
    || "the pupil";

  const formatOutcomeText = (text) => {
    if (!text || typeof text !== "string") return text;
    return text.replace(/\[REDACTED_NAME\]/gi, pupilDisplayName);
  };

  const parseManualLines = (text) => {
    return (text || "")
      .split(/\r?\n/)
      .map(s => s.replace(/^([•\-\*–]|\d+[\.\)])\s+/, "").trim())
      .filter(Boolean);
  };

  const saveManualTargets = async () => {
    if (!student?.id) {
      alert("Missing student id");
      return;
    }
    setSavingManual(true);
    try {
      const manualByArea = {
        communicationAndInteraction: parseManualLines(manualCni),
        cognitionAndLearning: parseManualLines(manualCal),
        socialEmotionalMentalHealth: parseManualLines(manualSemh),
        sensoryAndPhysical: parseManualLines(manualSp),
      };
      const raw = [
        ...manualByArea.communicationAndInteraction,
        ...manualByArea.cognitionAndLearning,
        ...manualByArea.socialEmotionalMentalHealth,
        ...manualByArea.sensoryAndPhysical,
      ].map(t => `• ${t}`).join("\n");

      // Write to student doc
      const studentRef = doc(db, `students/${student.id}`);
      await setDoc(studentRef, {
        ehcpTargetsByArea: manualByArea,
        ehcpTargetsRaw: raw,
      }, { merge: true });

      // Also write to summary doc when available
      const summaryDocId = getStudentEhcpSummaryDocId(student);
      if (summaryDocId) {
        const summaryRef = doc(db, "ehcp_summaries", summaryDocId);
        await setDoc(summaryRef, {
          targetsByArea: manualByArea,
          targets: raw,
        }, { merge: true });
      }

      // Update UI immediately
      setTargetsByArea(manualByArea);
      setTargets(raw);
      setShowTargets(true);
      setProcessingComplete(true);
      setProcessingStatus("Saved manual outcomes to database.");
      setManualMode(false);
    } catch (e) {
      console.error("Failed to save manual outcomes:", e);
      alert("Failed to save manual outcomes. See console.");
    } finally {
      setSavingManual(false);
    }
  };


  // Legacy testCloudFunction removed — processEHCP replaced by analyzeEHCP + extractEHCPSectionE

  const loadLatestEhcpSummary = async () => {
    const summaryDocId = getStudentEhcpSummaryDocId(student);
    if (summaryDocId) {
      const summarySnap = await getDoc(doc(db, "ehcp_summaries", summaryDocId));
      if (summarySnap.exists()) {
        return { id: summarySnap.id, data: summarySnap.data() };
      }
    }

    if (!student?.id || !student?.schoolId) return null;
    const snap = await getDocs(
      query(
        collection(db, "ehcp_summaries"),
        where("studentSchoolId", "==", student.schoolId),
        where("studentId", "==", student.id),
        orderBy("uploadedAt", "desc"),
        limit(1)
      )
    );
    if (snap.empty) return null;
    return { id: snap.docs[0].id, data: snap.docs[0].data() };
  };

  const checkIfSummaryExists = async () => {
    const storagePath = getStudentEhcpStoragePath(student);
    if (!storagePath && !getStudentEhcpSummaryDocId(student)) return false;

    try {
      const summary = await loadLatestEhcpSummary();
      if (!summary) return false;
      const data = summary.data;
      const t = data?.targets;
      const tba = data?.targetsByArea ? normalizeTargetsByArea(data.targetsByArea) : null;
      const sectionE = data?.sectionE ? normalizeTargetsByArea(data.sectionE) : null;
      const hasStructured = !!(
        (tba && (
          (Array.isArray(tba.communicationAndInteraction) && tba.communicationAndInteraction.length) ||
          (Array.isArray(tba.cognitionAndLearning) && tba.cognitionAndLearning.length) ||
          (Array.isArray(tba.socialEmotionalMentalHealth) && tba.socialEmotionalMentalHealth.length) ||
          (Array.isArray(tba.sensoryAndPhysical) && tba.sensoryAndPhysical.length)
        )) ||
        (sectionE && (
          sectionE.communicationAndInteraction.length ||
          sectionE.cognitionAndLearning.length ||
          sectionE.socialEmotionalMentalHealth.length ||
          sectionE.sensoryAndPhysical.length
        ))
      );
      return Boolean(t || hasStructured);
    } catch (error) {
      console.error("Error checking if summary exists:", error);
      return false;
    }
  };

  const startPolling = async () => {
    setPolling(true);
    setPollingCount(0);
    pollingCountRef.current = 0;
    setProcessingComplete(false);
    setShowSuccess(false);
    setProcessingStatus("Starting EHCP processing...");

    const poll = async () => {
      pollingCountRef.current += 1;
      setPollingCount(pollingCountRef.current);
      
      // Update status message based on polling count
      if (pollingCountRef.current < 6) {
        setProcessingStatus("Extracting text from PDF...");
      } else if (pollingCountRef.current < 12) {
        setProcessingStatus("Analyzing EHCP content with AI...");
      } else if (pollingCountRef.current < 24) {
        setProcessingStatus("Identifying learning targets...");
      } else {
        setProcessingStatus("Saving targets to database...");
      }
      
      const exists = await checkIfSummaryExists();
      if (exists) {
        setPolling(false);
        setProcessingComplete(true);
        setShowSuccess(true);
        setShowTargets(true);
        setProcessingStatus("Processing complete! Targets extracted successfully.");
        fetchTargets(); // Fetch the actual targets
        clearInterval(pollingIntervalRef.current);
        
        // Notify parent component that processing is complete
        if (onProcessingComplete) {
          onProcessingComplete();
        }
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else if (pollingCountRef.current >= 60) { // Stop after 5 minutes (60 * 5 seconds)
        // Quietly stop polling without surfacing a timeout message to the user
        setPolling(false);
        clearInterval(pollingIntervalRef.current);
      }
    };

    // Poll every 5 seconds
    pollingIntervalRef.current = setInterval(poll, 5000);
  };

  const triggerEHCPProcessing = async () => {
    const storagePath = getStudentEhcpStoragePath(student);
    if (!storagePath && !getStudentEhcpSummaryDocId(student)) {
      alert("No EHCP file path found for this student.");
      return;
    }

    setProcessing(true);
    setShowTargets(false);
    setProcessingComplete(false);
    setShowSuccess(false);
    setProcessingStatus("Initiating EHCP processing...");

    try {
      // Use the existing analyzeEHCP + extractEHCPSectionE callable functions.
      // First we need the ehcp_summaries docId for this student's EHCP.
      const { getDocs, query, where, collection } = await import('firebase/firestore');
      const { db: firestoreDb } = await import('../firebase');
      const snap = await getDocs(
        query(
          collection(firestoreDb, 'ehcp_summaries'),
          where('studentSchoolId', '==', student.schoolId),
          where('studentId', '==', student.id)
        )
      );

      if (snap.empty) {
        setProcessingStatus("No EHCP summary found. Please upload an EHCP first.");
        setProcessing(false);
        return;
      }

      const docId = snap.docs[0].id;
      const { analyzeEHCP, extractEHCPSectionE } = await import('../lib/functions');

      setProcessingStatus("Extracting text from PDF...");
      await analyzeEHCP({ docId });

      setProcessingStatus("Analysing EHCP content with AI...");
      await extractEHCPSectionE({ docId });

      setProcessingStatus("Processing complete! Targets extracted successfully.");
      await startPolling();
    } catch (error) {
      console.error("Error triggering EHCP processing:", error);
      setProcessingStatus("Processing failed. Please try again.");
      alert("Error triggering EHCP processing. Check console for details.");
    } finally {
      setProcessing(false);
    }
  };

  const fetchTargets = async () => {
    if (!student) {
      setLoading(false);
      setTargets("No student data provided.");
      return;
    }

    // Quiet logs in normal operation

    // Check if student has EHCP uploaded
    const storagePath = getStudentEhcpStoragePath(student);
    if (!storagePath && !getStudentEhcpSummaryDocId(student)) {
      setLoading(false);
      setShowTargets(false);
      setTargets("");
      return;
    }

    try {
      // Prefer targets from the student document if available
      if (student.ehcpTargetsRaw || student.ehcpTargetsByArea) {
        if (student.ehcpTargetsRaw) setTargets(student.ehcpTargetsRaw);
        if (student.ehcpTargetsByArea) {
          const normalizedFromStudent = normalizeTargetsByArea(student.ehcpTargetsByArea);
          setTargetsByArea(normalizedFromStudent);
          const anyStudentStructured =
            (normalizedFromStudent.communicationAndInteraction || []).length ||
            (normalizedFromStudent.cognitionAndLearning || []).length ||
            (normalizedFromStudent.socialEmotionalMentalHealth || []).length ||
            (normalizedFromStudent.sensoryAndPhysical || []).length;
          if (student.ehcpTargetsRaw || anyStudentStructured) {
            setShowTargets(true);
          }
        } else if (student.ehcpTargetsRaw) {
          setShowTargets(true);
        }
      }

      const summary = await loadLatestEhcpSummary();

      if (summary) {
        const data = summary.data;
        // Quiet logs
        // Raw bullet list (legacy)
        setTargets(data.targets || "");
        if (data.targets) {
          setShowTargets(true);
        }
        // Structured targets by area - support both new format (sectionE) and old format (targetsByArea)
        const sectionEData = data.sectionE || data.targetsByArea;
        if (sectionEData) {
          const normalized = normalizeTargetsByArea(sectionEData);
          // Quiet logs
          setTargetsByArea(normalized);
          const counts = {
            cni: (normalized.communicationAndInteraction || []).length,
            cal: (normalized.cognitionAndLearning || []).length,
            semh: (normalized.socialEmotionalMentalHealth || []).length,
            sp: (normalized.sensoryAndPhysical || []).length,
          };
          // Quiet logs
          if (!data.targets && (counts.cni || counts.cal || counts.semh || counts.sp)) {
            setProcessingComplete(true);
            setShowTargets(true);
            setProcessingStatus("Structured outcomes available.");
          }
          if (data.targets || counts.cni || counts.cal || counts.semh || counts.sp) {
            setShowTargets(true);
          }
        }
      } else {
        setTargets("No EHCP summary found for this student. The PDF may still be processing or the summary hasn't been created yet.");
      }
    } catch (error) {
      console.error("❌ Failed to fetch EHCP summary:", error);
      setTargets("Error fetching EHCP summary. Please check the console for details.");
    }

    setLoading(false);
  };

  // Check if targets exist on component mount
  useEffect(() => {
    const checkInitialTargets = async () => {
      if (getStudentEhcpStoragePath(student) || getStudentEhcpSummaryDocId(student)) {
        const exists = await checkIfSummaryExists();
        if (exists) {
          setShowTargets(true);
          setProcessingComplete(true);
          setProcessingStatus("Targets already extracted and ready to view.");
          
          // Notify parent component that processing is complete
          if (onProcessingComplete) {
            onProcessingComplete();
          }
        } else {
          // Auto-start polling since the Storage trigger runs on upload
          setProcessingStatus("No targets found yet. Monitoring for processing to complete...");
          startPolling();
        }
      }
    };
    
    checkInitialTargets();
    fetchTargets();
  }, [student]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  if (loading) return <p className="text-sm text-gray-500">Loading EHCP targets...</p>;

  // If no EHCP uploaded and no targets to show, only hide when manual editor is also hidden
  if (!getStudentEhcpStoragePath(student) && !getStudentEhcpSummaryDocId(student) && !showTargets && !manualMode) return null;

  return (
    <div className="mt-4 bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
      <h3 className="text-lg font-semibold mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Long-term outcomes (Section E)</h3>
      {/* Quick counts summary to make presence obvious */}
      <div className="text-xs text-gray-700 mb-2 flex flex-wrap gap-3">
        <span className="flex items-center"><Icons.ChatBubble className="w-4 h-4 mr-1" /> C&I: {targetsByArea.communicationAndInteraction.length}</span>
        <span className="flex items-center"><Icons.Brain className="w-4 h-4 mr-1" /> C&L: {targetsByArea.cognitionAndLearning.length}</span>
        <span className="flex items-center"><Icons.Heart className="w-4 h-4 mr-1" /> SEMH: {targetsByArea.socialEmotionalMentalHealth.length}</span>
        <span className="flex items-center"><Icons.Hand className="w-4 h-4 mr-1" /> S&P: {targetsByArea.sensoryAndPhysical.length}</span>
      </div>
      {/* Informational notice when no EHCP is uploaded */}
      {/* Removed message for missing EHCP upload as requested */}
      
      {/* Extraction and action buttons removed for manual-only mode */}
      
      {/* Categorized targets by area of need */}
      {showTargets && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <h4 className="font-semibold mb-2 text-gray-800 flex items-center"><Icons.ChatBubble className="w-4 h-4 mr-2" /> Communication & Interaction</h4>
            {targetsByArea.communicationAndInteraction.length > 0 ? (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {targetsByArea.communicationAndInteraction.map((t, i) => (
                  <li key={i}>{formatOutcomeText(t)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No outcomes found.</p>
            )}
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <h4 className="font-semibold mb-2 text-gray-800 flex items-center"><Icons.Brain className="w-4 h-4 mr-2" /> Cognition & Learning</h4>
            {targetsByArea.cognitionAndLearning.length > 0 ? (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {targetsByArea.cognitionAndLearning.map((t, i) => (
                  <li key={i}>{formatOutcomeText(t)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No outcomes found.</p>
            )}
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <h4 className="font-semibold mb-2 text-gray-800 flex items-center"><Icons.Heart className="w-4 h-4 mr-2" /> Social, Emotional & Mental Health</h4>
            {targetsByArea.socialEmotionalMentalHealth.length > 0 ? (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {targetsByArea.socialEmotionalMentalHealth.map((t, i) => (
                  <li key={i}>{formatOutcomeText(t)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No outcomes found.</p>
            )}
          </div>
          <div className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
            <h4 className="font-semibold mb-2 text-gray-800 flex items-center"><Icons.Hand className="w-4 h-4 mr-2" /> Sensory & Physical</h4>
            {targetsByArea.sensoryAndPhysical.length > 0 ? (
              <ul className="list-disc ml-5 text-sm space-y-1">
                {targetsByArea.sensoryAndPhysical.map((t, i) => (
                  <li key={i}>{formatOutcomeText(t)}</li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No outcomes found.</p>
            )}
          </div>

          
        </div>
      )}

      {!manualMode && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => {
              prefillManualFromCurrent();
              setManualMode(true);
            }}
            className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-yellow-300 bg-white text-yellow-900 hover:bg-yellow-100"
          >
            <Icons.PencilSquare className="w-4 h-4 mr-1.5" />
            Add or edit outcomes manually
          </button>
        </div>
      )}

      {/* Manual Entry Panel */}
      {manualMode && (
        <div className="mt-4 p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
          <h4 className="font-semibold mb-3 text-gray-800 flex items-center"><Icons.PencilSquare className="w-4 h-4 mr-2" /> Manual Outcomes (Section E)</h4>
          <p className="text-xs text-gray-600 mb-3">Paste one outcome per line under the appropriate area. Do not include provision from Section F.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.ChatBubble className="w-4 h-4 mr-1" /> Communication & Interaction</label>
              <textarea className="w-full border rounded p-2 text-sm h-32" value={manualCni} onChange={e => setManualCni(e.target.value)} placeholder="One outcome per line" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Brain className="w-4 h-4 mr-1" /> Cognition & Learning</label>
              <textarea className="w-full border rounded p-2 text-sm h-32" value={manualCal} onChange={e => setManualCal(e.target.value)} placeholder="One outcome per line" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Heart className="w-4 h-4 mr-1" /> Social, Emotional & Mental Health</label>
              <textarea className="w-full border rounded p-2 text-sm h-32" value={manualSemh} onChange={e => setManualSemh(e.target.value)} placeholder="One outcome per line" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 flex items-center"><Icons.Hand className="w-4 h-4 mr-1" /> Sensory & Physical</label>
              <textarea className="w-full border rounded p-2 text-sm h-32" value={manualSp} onChange={e => setManualSp(e.target.value)} placeholder="One outcome per line" />
            </div>
          </div>
          <div className="mt-3 flex items-center">
            <button onClick={saveManualTargets} disabled={savingManual} className="px-3 py-1 bg-emerald-600 text-white rounded text-sm hover:bg-emerald-700 disabled:opacity-50">
              {savingManual ? "Saving..." : <><Icons.Save className="w-4 h-4 mr-1 inline-block" /> Save Outcomes</>}
            </button>
            <button onClick={() => setManualMode(false)} disabled={savingManual} className="ml-2 px-3 py-1 bg-gray-300 text-gray-800 rounded text-sm hover:bg-gray-400 disabled:opacity-50">
              Cancel
            </button>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default EHCPExtractedTargets;
